/**
 * useSchoolHolidays
 *
 * Reactive state and actions for the school-holiday selector:
 * country toggle (DE/AT) → state dropdown → holiday dropdown.
 *
 * Holiday API responses are cached at module level in getHolidays.ts,
 * so reopening the modal does not re-fetch within the same session.
 *
 * @module composables/holidays/useSchoolHolidays
 */

import { ref, computed, type ComputedRef, type Ref } from 'vue';
import type { SelectOption } from '@/components/ui/BaseSelect.vue';
import type { DateRange } from '@/types/SearchBar';
import type { NBCState, NBCHoliday } from '@/types/schemas/nbc/holidays';
import { getStates, getSchoolHolidaysWithNextYear } from '@/services/api/nbc/getHolidays';
import { mapStatesToOptions, mapHolidaysToOptions } from '@/services/mappers/holidayMapper';

export type SupportedHolidayCountry = 'deutschland' | 'oesterreich';

export interface UseSchoolHolidaysReturn {
  selectedCountry: Ref<SupportedHolidayCountry>;
  selectedState: Ref<string | null>;
  stateOptions: ComputedRef<SelectOption[]>;
  holidayOptions: ComputedRef<SelectOption[]>;
  isLoadingStates: Ref<boolean>;
  isLoadingHolidays: Ref<boolean>;
  error: Ref<string | null>;
  selectCountry: (country: SupportedHolidayCountry) => Promise<void>;
  selectState: (state: string) => Promise<void>;
  optionToDateRange: (key: string) => DateRange;
}

export function useSchoolHolidays(
  getMinDate?: () => Date | null | undefined,
  getMaxDate?: () => Date | null | undefined
): UseSchoolHolidaysReturn {
  const selectedCountry = ref<SupportedHolidayCountry>('deutschland');
  const selectedState = ref<string | null>(null);

  const states = ref<NBCState[]>([]);
  const holidays = ref<NBCHoliday[]>([]);

  const isLoadingStates = ref(false);
  const isLoadingHolidays = ref(false);
  const error = ref<string | null>(null);

  const stateOptions = computed(() => mapStatesToOptions(states.value));
  const mappedHolidays = computed(() => mapHolidaysToOptions(holidays.value));
  const holidayOptions = computed(() => {
    const minDate = getMinDate?.() ?? null;
    const maxDate = getMaxDate?.() ?? null;
    if (!minDate && !maxDate) {
      return mappedHolidays.value.options;
    }
    return mappedHolidays.value.options.filter((option) => {
      const range = mappedHolidays.value.lookup.get(String(option.value));
      if (!range) return false;
      const [start, end] = range;
      if (minDate && end < minDate) return false;
      if (maxDate && start > maxDate) return false;
      return true;
    });
  });

  async function selectCountry(country: SupportedHolidayCountry): Promise<void> {
    selectedCountry.value = country;
    selectedState.value = null;
    holidays.value = [];
    states.value = [];
    error.value = null;
    isLoadingStates.value = true;
    try {
      const result = await getStates(country);
      // Discard stale response if user toggled country again while awaiting.
      if (selectedCountry.value !== country) return;
      states.value = result;
    } catch (err) {
      if (selectedCountry.value !== country) return;
      console.warn('[useSchoolHolidays] Failed to load states for', country, err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
      return;
    } finally {
      if (selectedCountry.value === country) {
        isLoadingStates.value = false;
      }
    }
  }

  async function selectState(state: string): Promise<void> {
    const country = selectedCountry.value;
    selectedState.value = state;
    holidays.value = [];
    error.value = null;
    isLoadingHolidays.value = true;
    try {
      const result = await getSchoolHolidaysWithNextYear(country, state);
      // Discard stale response if user changed state or country while awaiting.
      if (selectedState.value !== state || selectedCountry.value !== country) return;
      holidays.value = result;
    } catch (err) {
      if (selectedState.value !== state || selectedCountry.value !== country) return;
      console.warn('[useSchoolHolidays] Failed to load holidays for', country, state, err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      if (selectedState.value === state && selectedCountry.value === country) {
        isLoadingHolidays.value = false;
      }
    }
  }

  function optionToDateRange(key: string): DateRange {
    return mappedHolidays.value.lookup.get(key) ?? null;
  }

  return {
    selectedCountry,
    selectedState,
    stateOptions,
    holidayOptions,
    isLoadingStates,
    isLoadingHolidays,
    error,
    selectCountry,
    selectState,
    optionToDateRange,
  };
}
